import { Link, useParams } from "react-router";
import {
  ArrowLeft,
  ChevronDown,
  CircleCheck,
  CircleDot,
  FileSpreadsheet,
  FileText,
  Flag,
  Link2,
  ScanEye,
  Share2,
  Table,
  TriangleAlert,
} from "lucide-react";
import {
  REVIEW_STATE_LABELS,
  type AssessmentModule as ModuleRow,
  type ReviewState,
  type VisitDetail as VisitRecord,
  type VisitStatus,
} from "@taskiq/shared";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { EmptyState, ErrorState, Splash } from "@/components/ui/Feedback";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "@/components/ui/Menu";
import { useToast } from "@/components/ui/toast-context";
import { AssessmentPanel } from "@/components/visit/AssessmentPanel";
import { VisitSummary } from "@/components/visit/VisitSummary";
import { api, apiErrorMessage } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/lib/auth-context";
import { formatDayTimeIn, zoneOffset } from "@/lib/dates";
import { cn } from "@/lib/cn";
import { useTenancy } from "@/lib/tenancy";

/**
 * One visit, in full.
 *
 * A page rather than a drawer, for two reasons. There is far too much here for a
 * 28rem panel once the assessment record is on it. And a page has a URL you can
 * send somebody, which is the whole point when the thing being reviewed is
 * evidence - "look at this visit" should be a link, not a set of directions.
 *
 * ── The order is the order the questions get asked ─────────────────────────
 *
 * `VisitSummary` answers what was found and who found it, and the per-check
 * record is under it, because nobody scrolls to a ladder angle until the top of
 * the page has told them something is odd.
 *
 * ── Review is the console's only write against a visit ──────────────────────
 *
 * Flagging and signing off go to `PATCH /visits/:id { review_state }`, and that
 * is the whole of what this console may change. It cannot start a visit, cannot
 * complete one and cannot touch a reading - the trigger on `visits` compares the
 * whole row minus the review columns and refuses anything else, so the rule
 * holds regardless of what a browser sends. See decisions #36.
 */

export function VisitDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const tenancy = useTenancy();
  const toast = useToast();
  const timezone = tenancy.homeSite?.timezone ?? "UTC";
  const zoneLabel = zoneOffset(timezone);

  const record = useApi<{ visit: VisitRecord }>(id ? `/visits/${id}` : null);
  // The catalogue, for the checklist labels on the ladder and PPE cards. Loaded
  // once and cached by SWR across every visit the reader opens.
  const catalogue = useApi<{ assessment_modules: ModuleRow[] }>("/assessment-modules");

  const visit = record.data?.visit;

  if (record.isLoading) return <Splash label="Loading the visit" />;

  if (record.error || !visit) {
    return (
      <div className="page">
        <Link to="/app/visits" className="backlink">
          <ArrowLeft aria-hidden /> All visits
        </Link>
        <Card>
          {record.error ? (
            <ErrorState error={record.error} onRetry={() => void record.mutate()} />
          ) : (
            <EmptyState
              icon={<ScanEye />}
              title="That visit is not in this site's log"
              description="It may belong to another location, or the link may be out of date."
            />
          )}
        </Card>
      </div>
    );
  }

  const state = visit.review_state;

  async function setReview(next: ReviewState) {
    try {
      await api(`/visits/${visit!.id}`, { method: "PATCH", body: { review_state: next } });
      await record.mutate();
      toast.success(
        next === "flagged"
          ? "Flagged for review"
          : next === "reviewed"
            ? "Signed off"
            : "Flag cleared",
        { description: visit!.appointment.reference ?? visit!.appointment.title }
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update that visit"));
    }
  }

  async function copyLink() {
    const url = `${window.location.origin}/app/visits/${visit!.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // embedded browsers. Showing the URL is still a way to hand it over.
      toast.info("Copy the link from the address bar", { description: url });
    }
  }

  const ReviewIcon = state === "reviewed" ? CircleCheck : Flag;
  const canReview = can("visit.write");

  return (
    <div className="page">
      <div className="visit-header">
        <div className="visit-header__main">
          <Link to="/app/visits" className="visit-header__back" aria-label="All visits">
            <ArrowLeft aria-hidden />
          </Link>

          <div className="visit-header__title-block">
            <div className="visit-header__title-row">
              <h1 className="visit-header__title">{visit.appointment.title}</h1>
              <VisitStatusBadge status={visit.status} />
            </div>

            {/* The customer and the timezone the page is read in. The visit
                type moved into the record itself - see `VisitSummary` - because
                it is data about the visit rather than part of the page's name. */}
            <p className="visit-header__subtitle">
              {visit.appointment.customer_name} <span className="subtle">{zoneLabel}</span>
            </p>
          </div>
        </div>

        <div className="visit-header__actions">
          {canReview && (
            <Menu
              className={cn("visit-review", `visit-review--${state}`)}
              trigger={
                <>
                  <ReviewIcon aria-hidden />
                  {REVIEW_STATE_LABELS[state]}
                  <ChevronDown aria-hidden className="visit-review__chevron" />
                </>
              }
            >
              {visit.reviewed_at && (
                <>
                  <MenuLabel>{formatDayTimeIn(visit.reviewed_at, timezone)}</MenuLabel>
                  <MenuSeparator />
                </>
              )}
              <MenuItem
                icon={<Flag />}
                onClick={() => void setReview(state === "flagged" ? "open" : "flagged")}
              >
                {state === "flagged" ? "Clear flag" : "Flag for review"}
              </MenuItem>
              <MenuItem
                icon={<CircleCheck />}
                disabled={state === "reviewed"}
                onClick={() => void setReview("reviewed")}
              >
                Mark reviewed
              </MenuItem>
            </Menu>
          )}

          <Menu
            className="visit-share"
            trigger={
              <>
                <Share2 aria-hidden />
                Share
                <ChevronDown aria-hidden className="visit-share__chevron" />
              </>
            }
          >
            <MenuItem icon={<Link2 />} onClick={() => void copyLink()}>
              Copy link
            </MenuItem>
            <MenuSeparator />
            <MenuLabel>Export</MenuLabel>
            {/* All three read from the same visit record this page already
                has - the summary facts, the per-module checklist results and
                findings for a PDF report, the same rows flattened for Excel
                or CSV. Disabled until that renderer exists; listed now so the
                button does not have to change shape once it does. */}
            <MenuItem icon={<FileText />} disabled>
              Export as PDF
            </MenuItem>
            <MenuItem icon={<FileSpreadsheet />} disabled>
              Export as Excel
            </MenuItem>
            <MenuItem icon={<Table />} disabled>
              Export as CSV
            </MenuItem>
          </Menu>
        </div>
      </div>

      <VisitSummary visit={visit} timezone={timezone} />

      {visit.assessments.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ScanEye />}
            title="No checks were recorded"
            description="A visit opened on the phone carries its modules with it. This one has none, which usually means it was recorded before the capture flow ran."
          />
        </Card>
      ) : (
        <AssessmentPanel
          assessments={visit.assessments}
          modules={catalogue.data?.assessment_modules ?? []}
          timezone={timezone}
        />
      )}
    </div>
  );
}

/** The visit's own lifecycle, which is not the appointment's. */
function VisitStatusBadge({ status }: { status: VisitStatus }) {
  if (status === "in_progress") {
    return (
      <Badge tone="accent" icon={<CircleDot aria-hidden />}>
        In progress
      </Badge>
    );
  }
  if (status === "abandoned") {
    return (
      <Badge tone="caution" icon={<TriangleAlert aria-hidden />}>
        Abandoned
      </Badge>
    );
  }
  return (
    <Badge tone="ok" icon={<CircleCheck aria-hidden />}>
      Completed
    </Badge>
  );
}
